/**
 * Industry Overview - Wave 3 (2026-05-27).
 *
 * Surfaces the three-axis taxonomy that's the platform's
 * differentiator narrative: Industry Profile × Classification Family
 * × Compliance Bundle. Backend endpoint
 * (/v1/foundational/industry-overview) has been ready for weeks but
 * no page consumed it.
 */
import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import {
  Layers,
  Activity as ActivityIcon,
  AlertTriangle,
  Gavel,
  Building2,
  RefreshCw,
  Filter,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { KpiCard } from '@/components/viz/KpiCard';
import { LoadingState, ErrorState } from '@foctta/ui-shell';
import { EmptyStateV2 } from '@/components/ui/empty-state-v2';
import { useMotionPreset } from '@/lib/motion-level';
import {
  getIndustryOverview,
  getBuComplianceStatus,
  listBusinessUnits,
  type IndustryOverview,
  type BuComplianceStatus,
  type BusinessUnit,
} from '@/lib/api';

const DECISION_TYPE_COLOR: Record<string, string> = {
  allow: 'bg-success/80',
  mask: 'bg-info/80',
  step_up: 'bg-warning/80',
  block: 'bg-destructive/80',
  rate_limit: 'bg-warning/80',
};

export function IndustryOverviewPage(): JSX.Element {
  const [data, setData] = useState<IndustryOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<string | undefined>(undefined);
  const [family, setFamily] = useState<string | undefined>(undefined);
  const [bundle, setBundle] = useState<string | undefined>(undefined);
  const { transition, rise } = useMotionPreset();

  // BU compliance drawer state. Picks a BU and pulls
  // /v1/foundational/bu-compliance-status for the unified per-BU view.
  const [bus, setBus] = useState<BusinessUnit[]>([]);
  const [selectedBuId, setSelectedBuId] = useState<string | null>(null);
  const [buStatus, setBuStatus] = useState<BuComplianceStatus | null>(null);
  const [buLoading, setBuLoading] = useState(false);

  useEffect(() => {
    listBusinessUnits({ status: 'active' })
      .then((res) => setBus(res.businessUnits))
      .catch(() => {
        /* BU listing optional - page still works without it */
      });
  }, []);

  useEffect(() => {
    if (!selectedBuId) {
      setBuStatus(null);
      return;
    }
    setBuLoading(true);
    getBuComplianceStatus(selectedBuId)
      .then(setBuStatus)
      .catch(() => setBuStatus(null))
      .finally(() => setBuLoading(false));
  }, [selectedBuId]);

  async function refresh(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const filter: Parameters<typeof getIndustryOverview>[0] = {};
      if (profile) filter.industryProfile = profile;
      if (family) filter.classificationFamily = family;
      if (bundle) filter.complianceBundle = bundle;
      const result = await getIndustryOverview(filter);
      setData(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'load failed');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, family, bundle]);

  const totalDecisions = data?.decisions.total ?? 0;
  const decisionTypes = data ? Object.entries(data.decisions.byDecisionType) : [];

  return (
    <PageContainer>
      <PageHeader
        title="Industry Overview"
        subtitle="Three-axis taxonomy rollup: Industry Profile × Classification Family × Compliance Bundle. Findings + decisions aggregated across every Business Unit in this tenant."
        badges={[
          { label: 'Three-axis taxonomy', variant: 'glass' },
          { label: 'Cross-BU', variant: 'info' },
          ...(loading
            ? [{ label: 'Loading', variant: 'glass' as const }]
            : error
              ? [{ label: 'Degraded', variant: 'warning' as const }]
              : [{ label: 'Live', variant: 'success' as const }]),
        ]}
        actions={
          <Button size="sm" variant="glass" onClick={() => void refresh()} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        }
      />

      {/* Filter pills */}
      <GlassCard className="p-4 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Filter className="h-3.5 w-3.5" /> Filters:
          </div>
          <Input
            placeholder="Industry profile (e.g. bfsi.banking)"
            value={profile ?? ''}
            onChange={setProfile}
          />
          <Input
            placeholder="Classification family (e.g. bfsi.account-aggregator)"
            value={family ?? ''}
            onChange={setFamily}
          />
          <Input
            placeholder="Compliance bundle (e.g. bfsi_enterprise)"
            value={bundle ?? ''}
            onChange={setBundle}
          />
          {(profile || family || bundle) && (
            <Button
              size="sm"
              variant="glass"
              onClick={() => {
                setProfile(undefined);
                setFamily(undefined);
                setBundle(undefined);
              }}
            >
              Clear
            </Button>
          )}
        </div>
      </GlassCard>

      {loading && !data && <LoadingState caption="Loading industry rollup…" />}
      {error && (
        <ErrorState
          title="Failed to load industry overview"
          detail={error}
          onRetry={() => void refresh()}
        />
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <KpiCard
              label="Total findings"
              value={data.findings.total}
              icon={AlertTriangle}
              glow={data.findings.total > 0 ? 'medium' : 'low'}
              hint="across all BUs"
            />
            <KpiCard
              label="Critical"
              value={data.findings.critical}
              icon={AlertTriangle}
              glow={data.findings.critical > 0 ? 'critical' : 'low'}
              hint="severity-1"
            />
            <KpiCard
              label="High"
              value={data.findings.high}
              icon={AlertTriangle}
              glow={data.findings.high > 0 ? 'medium' : 'low'}
              hint="severity-2"
            />
            <KpiCard
              label="Decisions"
              value={totalDecisions}
              icon={Gavel}
              hint="last window"
            />
            <KpiCard
              label="Snapshots active"
              value={data.snapshotsActive}
              icon={Layers}
              hint="taxonomy resolves"
            />
          </div>

          {decisionTypes.length > 0 && (
            <motion.div initial={rise.hidden} animate={rise.visible} transition={transition}>
              <GlassCard className="p-5 mb-6">
                <CardHeader className="p-0 mb-4">
                  <CardTitle className="text-sm">Decision distribution</CardTitle>
                  <CardDescription>
                    Breakdown of decision types across all Business Units. {totalDecisions.toLocaleString()} decisions in
                    the active window.
                  </CardDescription>
                </CardHeader>
                <div className="space-y-2.5">
                  {decisionTypes
                    .sort((a, b) => b[1] - a[1])
                    .map(([type, count]) => {
                      const pct = totalDecisions > 0 ? (count / totalDecisions) * 100 : 0;
                      return (
                        <div key={type} className="space-y-1">
                          <div className="flex items-center justify-between text-xs">
                            <div className="flex items-center gap-2">
                              <Badge variant="glass" className="text-[9px] uppercase">
                                {type.replace(/_/g, ' ')}
                              </Badge>
                              <span className="text-muted-foreground tabular">
                                {count.toLocaleString()}
                              </span>
                            </div>
                            <span className="text-[10px] text-muted-foreground tabular">
                              {pct.toFixed(1)}%
                            </span>
                          </div>
                          <div className="h-1.5 w-full rounded-full bg-muted/40 overflow-hidden">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${pct}%` }}
                              transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
                              className={`h-full ${DECISION_TYPE_COLOR[type] ?? 'bg-primary/60'}`}
                            />
                          </div>
                        </div>
                      );
                    })}
                </div>
              </GlassCard>
            </motion.div>
          )}

          {/* Per-BU compliance status - same three-axis taxonomy, scoped to one BU. */}
          {bus.length > 0 && (
            <GlassCard className="p-5 mb-6">
              <CardHeader className="p-0 mb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-primary" /> Per-BU compliance status
                </CardTitle>
                <CardDescription>
                  Pick a Business Unit to see its specific findings, decisions, and active
                  taxonomy snapshots.
                </CardDescription>
              </CardHeader>
              <div className="flex flex-wrap items-center gap-2 mb-4">
                {bus.map((bu) => (
                  <Button
                    key={bu.id}
                    size="sm"
                    variant={selectedBuId === bu.id ? 'default' : 'glass'}
                    onClick={() =>
                      setSelectedBuId((current) => (current === bu.id ? null : bu.id))
                    }
                  >
                    <Building2 className="h-3 w-3 mr-1.5" />
                    {bu.name}
                  </Button>
                ))}
              </div>
              {buLoading && (
                <div className="glass-lite rounded-md p-4 text-xs text-muted-foreground">
                  Loading BU compliance status…
                </div>
              )}
              {buStatus && !buLoading && (
                <motion.div
                  initial={rise.hidden}
                  animate={rise.visible}
                  transition={transition}
                  className="grid grid-cols-2 md:grid-cols-4 gap-3"
                >
                  <BuStat
                    label="Findings (total)"
                    value={buStatus.findings.total}
                    sub={`${buStatus.findings.critical} critical · ${buStatus.findings.high} high`}
                  />
                  <BuStat
                    label="Decisions"
                    value={buStatus.decisions.total}
                    sub={`${Object.keys(buStatus.decisions.byDecisionType).length} types`}
                  />
                  <BuStat
                    label="Active taxonomy"
                    value={
                      buStatus.activeProfileIds.length +
                      buStatus.activeFamilyIds.length +
                      buStatus.activeBundleIds.length
                    }
                    sub={`${buStatus.activeProfileIds.length} profile · ${buStatus.activeFamilyIds.length} family · ${buStatus.activeBundleIds.length} bundle`}
                  />
                  <BuStat
                    label="Overlays"
                    value={buStatus.activeOverlayIds.length}
                    sub={buStatus.taxonomyVersion}
                  />
                </motion.div>
              )}
            </GlassCard>
          )}

          <GlassCard className="p-5">
            <CardHeader className="p-0 mb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Building2 className="h-4 w-4 text-primary" /> Active filters
              </CardTitle>
              <CardDescription>What the rollup is currently scoped by.</CardDescription>
            </CardHeader>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
              <FilterCard
                label="Industry profile"
                value={data.filter.industryProfile ?? 'All profiles'}
              />
              <FilterCard
                label="Classification family"
                value={data.filter.classificationFamily ?? 'All families'}
              />
              <FilterCard
                label="Compliance bundle"
                value={data.filter.complianceBundle ?? 'All bundles'}
              />
            </div>
            <p className="mt-4 text-[10px] text-muted-foreground">
              Generated at <code className="font-mono">{new Date(data.generatedAt).toLocaleString()}</code>
            </p>
          </GlassCard>
        </>
      )}

      {!loading && !data && !error && (
        <EmptyStateV2
          icon={Layers}
          title="No data for this filter combination"
          description="Try clearing filters, or check that this tenant has at least one Business Unit with active taxonomy snapshots."
        />
      )}
    </PageContainer>
  );
}

function Input({
  placeholder,
  value,
  onChange,
}: {
  placeholder: string;
  value: string;
  onChange: (v: string | undefined) => void;
}): JSX.Element {
  return (
    <input
      type="text"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value || undefined)}
      className="glass-lite px-3 py-1.5 rounded-md text-xs min-w-[200px] focus:outline-none focus:border-primary border border-border/40"
    />
  );
}

function FilterCard({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="glass-lite p-3 rounded-md">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-xs font-mono">{value}</div>
    </div>
  );
}

function BuStat({
  label,
  value,
  sub,
}: {
  label: string;
  value: number;
  sub?: string;
}): JSX.Element {
  return (
    <div className="glass-lite p-3 rounded-md">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 text-lg font-display tabular">{value.toLocaleString()}</div>
      {sub && <div className="mt-0.5 text-[10px] text-muted-foreground truncate">{sub}</div>}
    </div>
  );
}
